import React from 'react';
import { useFormContext } from 'react-hook-form';
import { checkStepCompletion, checkEvaluationStepCompletion } from '../utils/sectionCompletion';
import styles from './ProgressTracker.module.css';

interface Step {
  id: string;
  title: string;
}

interface ProgressTrackerProps {
  steps: Step[];
  currentStep: number;
  onStepClick: (stepIndex: number) => void;
  disabled?: boolean;
  isEvaluation?: boolean;
  evaluationSubmitted?: boolean;
  conflictDeclared?: boolean;
}

const ProgressTracker: React.FC<ProgressTrackerProps> = ({
  steps,
  currentStep,
  onStepClick,
  disabled = false,
  isEvaluation = false,
  evaluationSubmitted = false,
  conflictDeclared = false,
}) => {
  const { getValues } = useFormContext();
  const formData = getValues();
  const getStepItemClasses = (_isActive: boolean, isCompleted: boolean, isPending: boolean) => {
    const classes = [styles.stepItem];
    if (isCompleted) classes.push(styles.stepItemCompleted);
    else if (isPending) classes.push(styles.stepItemPending);
    return classes.join(' ');
  };

  const getStepCircleClasses = (isActive: boolean, isCompleted: boolean, isPending: boolean) => {
    const classes = [styles.stepCircle];
    if (isCompleted) classes.push(styles.stepCircleCompleted);
    else if (isActive) classes.push(styles.stepCircleActive);
    else if (isPending) classes.push(styles.stepCirclePending);
    return classes.join(' ');
  };

  const getStepTitleClasses = (isActive: boolean, isCompleted: boolean, isPending: boolean) => {
    const classes = [styles.stepTitle];
    if (isCompleted) classes.push(styles.stepTitleCompleted);
    else if (isActive) classes.push(styles.stepTitleActive);
    else if (isPending) classes.push(styles.stepTitlePending);
    return classes.join(' ');
  };

  return (
    <div className={styles.container}>
      <div className={styles.stepsContainer}>
        {steps.map((step, index) => {
          const isActive = index === currentStep;
          // Only check completion for previous steps
          let isCompleted = false;
          let isPending = false;
          
          if (index < currentStep) {
            try {
              let completed: boolean;
              if (isEvaluation) {
                // Special handling for evaluation steps
                if (step.id === 'summary' && evaluationSubmitted) {
                  completed = true;
                } else if (step.id === 'conflict-declaration' && conflictDeclared) {
                  completed = true;
                } else {
                  completed = checkEvaluationStepCompletion(step.id, formData);
                }
              } else {
                completed = checkStepCompletion(step.id, formData);
              }
              isCompleted = completed;
              isPending = !completed;
            } catch (error) {
              // Fallback to old behavior if form data is not ready
              isCompleted = true;
              isPending = false;
            }
          }

          return (
            <div
              key={step.id}
              className={getStepItemClasses(isActive, isCompleted, isPending)}
              onClick={() => !disabled && onStepClick(index)}
              style={{ cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1 }}
            >
              <div className={getStepCircleClasses(isActive, isCompleted, isPending)}>
                {isCompleted ? '✓' : isPending ? '!' : index + 1}
              </div>
              <span className={getStepTitleClasses(isActive, isCompleted, isPending)}>
                {step.title}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProgressTracker;
